import {
  Component,
  inject,
  ChangeDetectionStrategy,
  input,
  DestroyRef,
} from '@angular/core';
import { Workbook } from 'exceljs';
import { HumanizePipe } from '@app/core/pipes/humanize.pipe';
import { ExcelHelper, ExcelWriteBuffer } from '@app/core/helpers/excel.helper';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ErrorService } from '@app/core/services/error/error.service';

import { PivotReport, ToolbarComponent } from './toolbar/toolbar.component';
import {
  PivotTableField,
  RawPivotTableComponent,
} from '../raw-pivot-table/raw-pivot-table.component';
import { Ag1ButtonComponent } from '../ag1-button/ag1-button.component';
import { NgTemplateOutlet } from '@angular/common';
import { FieldDrawerComponent } from './field-drawer/field-drawer.component';
import { Ag1LoaderComponent } from '../ag1-loader/ag1-loader.component';
import { catchError, EMPTY, Observable } from 'rxjs';

@Component({
  selector: 'ag-pivot-table',
  templateUrl: './pivot-table.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ToolbarComponent,
    Ag1ButtonComponent,
    NgTemplateOutlet,
    FieldDrawerComponent,
    Ag1LoaderComponent,
  ],
})
export class PivotTableComponent extends RawPivotTableComponent {
  private _destroyRef = inject(DestroyRef);
  private humanizePipe = inject(HumanizePipe);
  private readonly _errorService = inject(ErrorService);

  readonly possibleFields = input<PivotTableField[]>(undefined);

  readonly configType = input<string>(undefined);

  readonly showFieldToggle = input(true);

  readonly canSaveAsPublic = input(false);

  readonly isLoading = input<boolean>(true);

  showFields = false;

  constructor() {
    super();
  }

  override rePivot(): void {
    this.reRender();
  }

  reRender() {
    super.rePivot();
  }

  handleFieldConfigChange() {
    this.reRender();
  }

  handleConfigClick() {
    this.showFields = !this.showFields;
  }

  handleReportChange(event: PivotReport) {
    this.rows.set(event.rows);
    this.cols.set(event.cols);
    this.values.set(event.values);

    this.reRender();
  }

  // mirrors the v1 cash-flow's export format
  // doesn't actually export the pivoted table, but rather the minimal data set required to make that pivot
  exportExcel(fileName: string): void {
    this._exportExcel$(fileName).subscribe();
  }

  private _exportExcel$(fileName: string): Observable<ExcelWriteBuffer> {
    const hsh = {};
    const data = this.data();
    const rows = this.rows();
    const cols = this.cols();
    const values = this.values();

    data.forEach((element) => {
      this.aggregateV2(hsh, element, 0, [...rows, ...cols], values);
    });

    const aggregatedHeaders = [...rows, ...cols, ...values];

    const minimalDataSet = [];

    minimalDataSet.push(aggregatedHeaders.map((h) => h.field));

    data.forEach((originalItem) => {
      const minimalItem = [];

      aggregatedHeaders.forEach((header) => {
        minimalItem.push(originalItem[header.field]);
      });

      minimalDataSet.push(minimalItem);
    });

    const workbook = new Workbook();
    ExcelHelper.addAoASheet(workbook, 'Sheet 1', minimalDataSet);
    return ExcelHelper.downloadWorkbook$(
      workbook,
      fileName + '-' + new Date().toISOString() + '.xlsx',
    ).pipe(
      catchError((error) => {
        this._errorService.handleError(error, 'Failed to export Excel file');
        return EMPTY;
      }),
      takeUntilDestroyed(this._destroyRef),
    );
  }
}
